<template>
  <div class="article-with-sidebar">
    <div class="layout">
      <div class="content">
        <ArticleTags :tags="article.news_tags" />
        <h1>{{ article.name }}</h1>
        <ArticleView :article="article" @vue:mounted="onContentRendered" />

        <div v-if="isMobileOrTablet" class="mobile-sidebar">
          <ArticleActions :article="article" @scroll-to-comments="scrollToComments" />
          <ArticleNavContent ref="mobileNavRef" />
          <RuralTycoonSideBanner />
        </div>

        <ArticleComments
          :slug="article.slug"
          :initial-comments="article.comments"
          :show-comments="article.is_show_comments"
          @update-comments="emit('update-comments')"
        />

        <slot name="after" />
      </div>

      <div v-if="isDesktop" class="sidebar">
        <div class="sidebar-sticky">
          <ArticleActions :article="article" @scroll-to-comments="scrollToComments" />
          <ArticleNavContent ref="navRef" />
          <RuralTycoonSideBanner />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { Article } from '~/models/articles'
  import ArticleTags from './ArticleTags.vue'
  import ArticleView from './ArticleView.vue'
  import ArticleActions from './ArticleActions.vue'
  import ArticleNavContent from './ArticleNavContent.vue'
  import ArticleComments from '~/components/article/comments/ArticleComments.vue'
  import { RuralTycoonSideBanner } from '~/components/banners'

  defineProps<{
    article: Article
  }>()

  const emit = defineEmits<{
    (e: 'update-comments'): void
  }>()

  const { isDesktop } = useBreakpoint()
  const isMobileOrTablet = computed(() => !isDesktop.value)

  const navRef = ref<InstanceType<typeof ArticleNavContent> | null>(null)
  const mobileNavRef = ref<InstanceType<typeof ArticleNavContent> | null>(null)

  const onContentRendered = () => {
    nextTick(() => {
      if (isDesktop.value && navRef.value) {
        navRef.value.initObserver()
      }
      if (!isDesktop.value && mobileNavRef.value) {
        mobileNavRef.value.initObserver()
      }
    })
  }

  const scrollToComments = () => {
    const el = document.querySelector('.article-comments')
    if (!el) return

    const headerHeight = document.querySelector('header')?.offsetHeight || 100
    window.scrollTo({
      top: (el as HTMLElement).offsetTop - headerHeight,
      behavior: 'smooth',
    })
  }

  onBeforeUnmount(() => {
    navRef.value?.disableObserver()
    mobileNavRef.value?.disableObserver()
  })
</script>

<style lang="scss" scoped>
  .article-with-sidebar {
    @extend %flex-column;

    gap: 24px;
    width: 100%;

    > .layout {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 312px;
      gap: 36px;

      @include vp-1279 {
        display: block;
      }
    }

    > .layout > .content {
      @extend %flex-column;

      gap: 24px;
      min-width: 0;

      > h1 {
        font-size: 40px;
        font-weight: 500;
        line-height: 48px;

        @include vp-1279 {
          font-size: 32px;
          line-height: 40px;
        }

        @include vp-767 {
          font-size: 24px;
          line-height: 32px;
        }
      }

      > .mobile-sidebar {
        @extend %flex-column;

        gap: 24px;
      }
    }

    > .layout > .sidebar {
      min-width: 0;

      @include vp-1279 {
        display: none;
      }
    }

    > .layout > .sidebar > .sidebar-sticky {
      position: sticky;
      top: calc(24px + 110px);
      @extend %flex-column;

      gap: 24px;

      @include vp-1439 {
        top: calc(24px + 62px);
      }
    }
  }
</style>
